import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { QuoteLineItemRow, type LineItemDraft } from "./QuoteLineItemRow";

/** A line item in the quote form, with a client-only key that stays with it across reorders. */
export type FormLineItem = LineItemDraft & { key: string };

/**
 * Resolve a finished drag to the [from, to] indices to move, or null when
 * nothing should change (dropped outside the list, onto itself, or an id we
 * don't know). Exported for unit tests.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function moveLineItem(
  keys: string[],
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier | null | undefined,
): [number, number] | null {
  if (overId == null) return null;
  const from = keys.indexOf(String(activeId));
  const to = keys.indexOf(String(overId));
  if (from === -1 || to === -1 || from === to) return null;
  return [from, to];
}

/** Renders a row in place of QuoteLineItemRow, given the drag handle (the quote drawer, #1559). */
export type RenderLineItemRow = (item: FormLineItem, index: number, handle: ReactNode) => ReactNode;

interface SortableRowProps {
  item: FormLineItem;
  index: number;
  onChange: (index: number, field: string, value: string | number) => void;
  onRemove: (index: number) => void;
  renderRow?: RenderLineItemRow;
}

function SortableRow({ item, index, onChange, onRemove, renderRow }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.key });

  const handle = (
    <button
      // The rows sit inside the quote <form>; an untyped button would submit it.
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      aria-label={`Reorder ${item.name || "line item"}`}
      className="h-8 w-6 shrink-0 flex items-center justify-center rounded text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "relative z-10 opacity-80 shadow-md rounded-lg" : undefined}
    >
      {renderRow ? (
        renderRow(item, index, handle)
      ) : (
        <QuoteLineItemRow item={item} index={index} onChange={onChange} onRemove={onRemove} dragHandle={handle} />
      )}
    </div>
  );
}

interface SortableLineItemListProps {
  items: FormLineItem[];
  onChange: (index: number, field: string, value: string | number) => void;
  onRemove: (index: number) => void;
  onReorder: (from: number, to: number) => void;
  /** Fires true when a drag starts and false when it ends or is cancelled. */
  onDraggingChange?: (dragging: boolean) => void;
  /** Each row's content; the template editor passes none and gets QuoteLineItemRow. */
  renderRow?: RenderLineItemRow;
}

export function SortableLineItemList({ items, onChange, onRemove, onReorder, onDraggingChange, renderRow }: SortableLineItemListProps) {
  const sensors = useSensors(
    // PointerSensor covers mouse and touch; the handle is touch-none so a drag
    // doesn't scroll the dialog. The small distance keeps a plain click inert.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const keys = useMemo(() => items.map((li) => li.key), [items]);
  const draggingRef = useRef(false);
  const onDraggingChangeRef = useRef(onDraggingChange);
  onDraggingChangeRef.current = onDraggingChange;
  const setDragging = useCallback((dragging: boolean) => {
    draggingRef.current = dragging;
    onDraggingChangeRef.current?.(dragging);
  }, []);

  // If the list unmounts mid-drag (dialog saved or closed, last row removed),
  // dnd-kit leaves the active sensor listening on the document: it swallows
  // Space/Enter/arrows and later fires this stale list's onDragEnd against the
  // next quote's rows. Both sensors cancel on a window resize, so send one.
  useEffect(
    () => () => {
      if (!draggingRef.current) return;
      window.dispatchEvent(new Event("resize"));
      setDragging(false);
    },
    [setDragging],
  );

  // dnd-kit's default screen-reader text reads out the ids, which are random
  // UUIDs here; describe rows by name and position instead.
  const announcements = useMemo<Announcements>(() => {
    const indexOf = (id: UniqueIdentifier) => items.findIndex((li) => li.key === id);
    const nameOf = (id: UniqueIdentifier) => items[indexOf(id)]?.name || "line item";
    const at = (id: UniqueIdentifier) => `position ${indexOf(id) + 1} of ${items.length}`;
    return {
      onDragStart: ({ active }) => `Picked up ${nameOf(active.id)}, ${at(active.id)}.`,
      onDragOver: ({ active, over }) => (over ? `${nameOf(active.id)} moved to ${at(over.id)}.` : undefined),
      onDragEnd: ({ active, over }) =>
        over ? `Dropped ${nameOf(active.id)} at ${at(over.id)}.` : `Dropped ${nameOf(active.id)}; order unchanged.`,
      onDragCancel: ({ active }) => `Cancelled. ${nameOf(active.id)} stays at ${at(active.id)}.`,
    };
  }, [items]);

  const handleDragStart = useCallback(() => setDragging(true), [setDragging]);
  const handleDragCancel = useCallback(() => setDragging(false), [setDragging]);
  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setDragging(false);
      const move = moveLineItem(keys, active.id, over?.id);
      if (move) onReorder(move[0], move[1]);
    },
    [keys, onReorder, setDragging],
  );

  return (
    <DndContext
      sensors={sensors}
      accessibility={{ announcements }}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={keys} strategy={verticalListSortingStrategy}>
        {items.map((li, i) => (
          <SortableRow key={li.key} item={li} index={i} onChange={onChange} onRemove={onRemove} renderRow={renderRow} />
        ))}
      </SortableContext>
    </DndContext>
  );
}
